/**
 * portal-search.manager
 * @namespace portal
 */

(function () {
	'use strict';

	/** add the service */
	angular.module('nha.portal').service('portal-search.manager', portalSearchManager);

	/** injection */
	portalSearchManager.$inject = ['$q', '$http', 'appSettings'];

	/** implementation */
	function portalSearchManager($q, $http, appSettings) {
		/** capture service object */
		var service = this;

		/** private properties */
		var searchOptions = [
			{ 'type': 'student', 'name': 'Students', 'placeholder': 'Enter Student Name, ID, or type "Birthdays" to see student birthdays' },
			{ 'type': 'staff', 'name': 'Staff', 'placeholder': 'Enter Staff Member Name' },
			{ 'type': 'parent', 'name': 'Parents', 'placeholder': 'Enter Parent Name, Email, or Phone' }
		];
		var pageSize = 20;
		var loadedCount = 0;
		var moreExist = true;
		var currentResults = [];
		var currentCount = 0;
		var currentSearchCriteria = '';

		/** public methods */
		service.getSearchOptions = getSearchOptions;
		service.resetPagingOptions = resetPagingOptions;
		service.searchStudents = searchStudents;
		service.searchParents = searchParents;
		service.searchStaff = searchStaff;

		/** public method implementations */

		/** return the search options */
		function getSearchOptions() {
			searchOptions[0].active = true;
			return searchOptions;
		}

		/** reset the paging options */
		function resetPagingOptions() {
			pageSize = 20;
			loadedCount = 0;
			moreExist = true;
			currentResults = [];
			currentCount = 0;
			currentSearchCriteria = '';
		}

		/** search students */
		function searchStudents(schoolIDs, academicYearID, searchCriteria, cancelor) {
			/** create a promise */
			var deferred = $q.defer();

			/** check the search criteria */
			if (currentSearchCriteria !== searchCriteria) {
				/** reset the paging options */
				resetPagingOptions();
			}

			/** set the current search criteria */
			currentSearchCriteria = searchCriteria;

			/** get the params */
			var params = getParams(schoolIDs, academicYearID);
			params.searchText = searchCriteria;
			params.skip = loadedCount;
			params.take = pageSize;

			/** check for moreExist */
			if (!moreExist) {
				/** return last loaded data */
				deferred.resolve({ 'count': currentCount, 'items': currentResults });
				return deferred.promise;
			}

			/** make the request */
			$http.get(appSettings.portalServicesRootUrl + 'studentsearch', { 'params': params, 'cache': true, 'timeout': cancelor.promise }).then(function(results) {
				/** manipulate parent results */
				_.forEach(results.data.items, function(s) {
					/** adjust the grade level name */
					s.gradeLevelNameAdjusted = adjustGradeLevelName(s.gradeLevelName);
				});

				/** set paging variables */
				loadedCount += results.data.items.length;
				if (results.data.items.length < pageSize) moreExist = false;

				/** set the current context */
				currentResults = currentResults.concat(results.data.items);
				currentCount = results.data.count;
				deferred.resolve({ 'count': currentCount, 'items': currentResults });
			});

			/** return the promise */
			return deferred.promise;
		}

		/** search staff members */
		function searchStaff(schoolIDs, academicYearID, searchCriteria, cancelor) {
			/** create a promise */
			var deferred = $q.defer();

			/** check the search criteria */
			if (currentSearchCriteria !== searchCriteria) {
				/** reset the paging options */
				resetPagingOptions();
			}

			/** set the current search criteria */
			currentSearchCriteria = searchCriteria;

			/** get the params */
			var params = getParams(schoolIDs, academicYearID);

			/** search by name */
			params.staffName = currentSearchCriteria;

			/** add additional odata params */
			params.orderby = 'LastName, FirstName';
			params.top = pageSize;
			params.skip = loadedCount;

			/** check for moreExist */
			if (!moreExist) {
				/** return last loaded data */
				deferred.resolve({ 'count': currentCount, 'items': currentResults });
				return deferred.promise;
			}

			/** make the request */
			$http.get(appSettings.portalServicesRootUrl + 'staffsearch', { 'params': params, 'cache': true, 'timeout': cancelor.promise }).then(function(results) {
				/** set paging variables */
				loadedCount += results.data.items.length;
				if (results.data.items.length < pageSize) moreExist = false;

				/** set the current context */
				currentResults = currentResults.concat(results.data.items);
				currentCount = results.data.count;
				deferred.resolve({ 'count': currentCount, 'items': currentResults });
			});

			/** return the promise */
			return deferred.promise;
		}

		/** search parents */
		function searchParents(schoolIDs, academicYearID, searchCriteria, cancelor) {
			/** create a promise */
			var deferred = $q.defer();

			/** check the search criteria */
			if (currentSearchCriteria !== searchCriteria) {
				/** reset the paging options */
				resetPagingOptions();
			}

			/** set the current search criteria */
			currentSearchCriteria = searchCriteria;

			/** get the params */
			var params = getParams(schoolIDs, academicYearID);

			/** try to parse out a phone number */
			var phoneNumber = parseNumber(currentSearchCriteria);
			if (phoneNumber) {
				/** check the phone number length */
				if (phoneNumber.length < 5) {
					/** create a promise */
					deferred.resolve({ message: 'Phone numbers require at least 5 digits' });
					return deferred.promise;
				}

				/** add the phone number param */
				params.phoneNumber = phoneNumber;
			} else {
				/** search by name */
				params.searchString = currentSearchCriteria;
			}

			/** add additional odata params */
			params.$orderby = 'LastName, FirstName';
			params.$inlinecount = 'allpages';
			params.$top = pageSize;
			params.$skip = loadedCount;

			/** check for moreExist */
			if (!moreExist) {
				/** return last loaded data */
				deferred.resolve({ 'count': currentCount, 'items': currentResults });
				return deferred.promise;
			}

			/** make the request */
			$http.get(appSettings.portalServicesRootUrl + 'parentsearch', { 'params': params, 'cache': true, 'timeout': cancelor.promise }).then(function(results) {
				/** manipulate parent results */
				_.forEach(results.data.items, function(p) {
					/** set opt out values */
					p.optOut = false;
					if (p.doNotCall) p.optOut = true;
					if (p.doNotText) p.optOut = true;
					if (p.doNotEmail) p.optOut = true;
					if (p.doNotMail) p.optOut = true;

					/** order the phone numbers by type */
					p.phoneNumbers.sort(function(a, b) {
						if (a.type < b.type) return -1;
						if (a.type > b.type) return 1;
						return 0;
					});

					/** add students object */
					p.studentGroups = {};

					/** cycle through students */
					_.forEach(p.students, function(s) {
						/** adjust the grade level name */
						s.gradeLevelNameAdjusted = adjustGradeLevelName(s.gradeLevelName, s.gradeLevelNameDesc);

						/** check for student group */
						if (!p.studentGroups[s.studentID]) p.studentGroups[s.studentID] = {
							'studentID': s.studentID,
							'firstName': s.firstName,
							'lastName': s.lastName,
							'studentInSchool': []
						};

						/** add the student record */
						p.studentGroups[s.studentID].studentInSchool.push(s);
					});
				});

				/** set paging variables */
				loadedCount += results.data.items.length;
				if (results.data.items.length < pageSize) moreExist = false;

				/** set the current context */
				currentResults = currentResults.concat(results.data.items);
				currentCount = results.data.count;
				deferred.resolve({ 'count': currentCount, 'items': currentResults });
			});

			/** return the promise */
			return deferred.promise;
		}

		/** private methods */

		/** get base parameters */
		function getParams(schoolIDs, academicYearID) {
			/** setup params */
			var params = {
				'academicYearID': academicYearID
			};

			/** parse integers out of school ids */
			schoolIDs = _.map(schoolIDs, _.parseInt);
			if (!_.includes(schoolIDs, 0)) {
				params.schoolIDs = schoolIDs;
			}

			/** return the params */
			return params;
		}

		/** parse a number from the string */
		function parseNumber(n) {
			var numArr = n.match(/\d/g);
			return numArr ? numArr.join('') : null;
		}

		/** adjust the grade level name */
		function adjustGradeLevelName(gradeLevelName) {
			if (gradeLevelName === 'K') return 'Kindergarten';
			if (gradeLevelName === '1') return gradeLevelName + 'st Grade';
			if (gradeLevelName === '2') return gradeLevelName + 'nd Grade';
			if (gradeLevelName === '3') return gradeLevelName + 'rd Grade';
			if (['4', '5', '6', '7', '8'].contains(gradeLevelName)) return gradeLevelName + 'th Grade';
			return gradeLevelName;
		}

		/** return the service */
		return service;
	}
})();